'use client';

import React from 'react';
import styled from 'styled-components';
import { FaStar } from '@/components/icons';
import Link from 'next/link';
import { COLORS } from '@/constants/colors';


const REVIEWS = [
  {
    id: 0,
    title: 'I installed a Green Filter on my 2008…',
    text: "I installed a Green Filter on my 2008 Porsche 997 Turbo, and the difference was noticeable immediately. The engine feels more responsive, performance has improved, and the intake sound is even better—exactly what I was hoping for. I have performance filters from competing brands on several of my other cars because they came with aftermarket cold air intake systems. After experiencing the results with Green Filter, I'll be replacing all of them with Green Filters. I highly recommend this product to anyone looking for a simple upgrade that delivers real performance and sound improvements. P.S. As a nice piece of racing heritage, the Ford GT that won the 2016 24 Hours of Le Mans was equipped with Green Filter.",
    author: 'Carlos',
    rating: 5,
    date: 'August 3, 2026',
    sourceUrl: 'https://www.trustpilot.com/reviews/6a70236d0ad8fd913e3fd9c1',
  },
  {
    id: 1,
    title: "26' Maverick EB",
    text: "Ordered this filter for my 26' Maverick, and it fit perfectly. The shipping was super quick! Couldn't be happier!",
    author: 'Rick Brown',
    rating: 5,
    date: 'July 24, 2026',
    sourceUrl: 'https://www.trustpilot.com/reviews/6a6387255f5966aa571408a3',
  },
  {
    id: 2,
    title: 'Love my green filters!!!',
    text: 'Love my green filters. Must be honest and say I bought filters originally based on great reputation / reviews and also the great look !!!! Love the green ! Bought for both my track Gt3rs and my porsche turbo. Actually noticed fuel mileage improvements and very happy with flow! Just ordered my clean kit and looking forward to cleaning them !!',
    author: 'Jr',
    rating: 5,
    date: 'June 30, 2026',
    sourceUrl: 'https://www.trustpilot.com/reviews/6a43d743737b8a357d622fc3',
  },
  {
    id: 3,
    title: 'Great product',
    text: 'Great product. Switched from K&N to Green Filter for the first time and so far very very happy.',
    author: 'George Arias',
    rating: 5,
    date: 'June 26, 2026',
    sourceUrl: 'https://www.trustpilot.com/reviews/6a3eb2b3cd6a7db9628fc5e9',
  },
];

interface HomeElement2Props {
  title?: string;
  subtitle?: string;
}

const HomeElement2 = ({
  title = 'What Customers Say About Green Filter',
  subtitle = 'Drivers choose Green Filter for better airflow, easy installation, reusable performance, and long-term value. Here is what customers have experienced after upgrading from a standard paper filter.'
}: HomeElement2Props) => {
  const [expandedIds, setExpandedIds] = React.useState<Set<number>>(new Set());

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <Section>
      <HeadingBlock>
        <Headline>{title}</Headline>
        <Accent />
        <Subtitle>{subtitle}</Subtitle>
      </HeadingBlock>

      <Grid>
        {REVIEWS.map((review) => {
          const isLong = review.text.length > 220;
          const isExpanded = expandedIds.has(review.id);

          return (
          <Card key={review.id}>
            <StarsRow aria-label={`${review.rating} star review`}>
              {Array.from({ length: review.rating }).map((_, i) => (
                <FaStar key={i} />
              ))}
            </StarsRow>
            <ReviewTitle>{review.title}</ReviewTitle>
            <ReviewText $expanded={isExpanded}>{review.text}</ReviewText>
            {isLong && (
              <ReadMoreButton type="button" onClick={() => toggleExpand(review.id)}>
                {isExpanded ? 'Read less' : 'Read more'}
              </ReadMoreButton>
            )}
            <Divider />
            <ProfileRow>
              <AvatarInitial aria-hidden="true">{review.author.charAt(0)}</AvatarInitial>
              <ProfileInfo>
                <AuthorName>{review.author}</AuthorName>
                <SourceRow>
                  <SourceLink href={review.sourceUrl} target="_blank" rel="noopener noreferrer">
                    Verified review on Trustpilot ↗
                  </SourceLink>
                  <ReviewDate>{review.date}</ReviewDate>
                </SourceRow>
              </ProfileInfo>
            </ProfileRow>
          </Card>
          );
        })}
      </Grid>

      <CTAWrapper>
        <Link href="/shop-by-vehicle">
          <CTAButton>Find Your Green Filter</CTAButton>
        </Link>
      </CTAWrapper>
    </Section>
  );
};

export default React.memo(HomeElement2);

const Section = styled.section`
  background: #fff;
  padding: 80px 24px;

  @media (max-width: 767px) {
    padding: 48px 0;
  }
`;

const HeadingBlock = styled.div`
  max-width: 640px;
  margin: 0 auto 56px;
  text-align: center;

  @media (max-width: 767px) {
    margin-bottom: 40px;
    padding: 0 16px;
  }
`;

const Headline = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 36px;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.2;

  @media (max-width: 991px) {
    font-size: 32px;
  }

  @media (max-width: 767px) {
    font-size: 28px;
  }

  @media (max-width: 479px) {
    font-size: 24px;
  }
`;

const Accent = styled.div`
  width: 56px;
  height: 3px;
  background: ${COLORS.brandGreen};
  border-radius: 2px;
  margin: 14px auto 16px;
`;

const Subtitle = styled.p`
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #666;
  margin: 0;
  line-height: 1.6;

  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const Grid = styled.div`
  max-width: 960px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 767px) {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    gap: 16px;
    padding: 0 16px 16px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.11);
  }

  @media (max-width: 767px) {
    flex: 0 0 85vw;
    max-width: 340px;
    scroll-snap-align: start;
  }
`;

const StarsRow = styled.div`
  display: flex;
  gap: 3px;
  color: ${COLORS.brandGreen};
  font-size: 15px;
  margin-bottom: 14px;
`;

const ReviewTitle = styled.h3`
  font-family: Lato, sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 10px;
  line-height: 1.3;

  @media (max-width: 767px) {
    font-size: 18px;
  }
`;

const ReviewText = styled.p<{ $expanded?: boolean }>`
  font-family: Lato, sans-serif;
  font-size: 14px;
  color: #555;
  line-height: 1.65;
  margin: 0 0 20px;
  flex: 1;

  ${({ $expanded }) =>
    !$expanded &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

const ReadMoreButton = styled.button`
  font-family: Lato, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.brandGreen};
  background: none;
  border: none;
  padding: 0;
  margin: -14px 0 20px;
  cursor: pointer;
  align-self: flex-start;

  &:hover {
    text-decoration: underline;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #f0f0f0;
  margin: 0 0 18px;
`;

const ProfileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AvatarInitial = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.brandGreen};
  color: #fff;
  font-family: Lato, sans-serif;
  font-size: 20px;
  font-weight: 700;
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const AuthorName = styled.span`
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
`;

const SourceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  flex-wrap: wrap;
`;

const SourceLink = styled.a`
  font-family: Lato, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.brandGreen};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ReviewDate = styled.span`
  font-family: Lato, sans-serif;
  font-size: 12px;
  color: #888;
`;

const CTAWrapper = styled.div`
  margin-top: 48px;
  text-align: center;

  @media (max-width: 767px) {
    padding: 0 16px;
  }
`;

const CTAButton = styled.button`
  background-color: #00ad23;
  background-image: none;
  min-width: 64px;
  color: #fff;
  padding: 6px 16px;
  border-radius: 4px;
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.75;
  letter-spacing: 0.02857em;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #00901c;
  }
`;
